import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import TabsNavigation from 'components/uiLibrary/TabsNavigation';

import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';

import { DATE_FORMATS, TP } from 'constants/index';
import { PRODUCTS_WITH_CASTING_TOOL_ACCESS } from 'constants/subscriptions';

import { convertRoleDebutExpression, undoRoleDebutExpression } from 'utils/casting';
import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageAccessContext from 'utils/hooks/usePageAccessContext';
import usePageContext from 'utils/hooks/usePageContext';
import useSalesDemoForOrgContext from 'utils/hooks/useSalesDemoForOrgContext';
import { useQuery } from 'utils/react-query';

import orgQueries from 'containers/Organizations/queries';

import {
  CASTING_RESULT_TYPES_VALUES,
  ENTITY_TYPES,
  FILTER_TYPES,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import classes from './CastingResultTypeTabs.module.scss';

const CastingResultTypeTabs = ({ trackingData, sticky = false, asDropdown = false }) => {
  const { applyFilters, permissions } = usePageContext();
  const { salesDemoAccountInfo } = useSalesDemoForOrgContext();
  const { obRouteContext, isLoggedIn } = useAppContext();
  const showPageAccessPortalForCasting = useShowPageAccessPortalForCasting();
  const { activePageAccessContext } = usePageAccessContext();
  const { setIsPermissionDeniedModalOpen, setIsJumpInSelectProductionsModalOpen, setIsLoginDialog } = useDialogs();
  const { linkProps = {}, filterParams = {} } = obRouteContext;
  const { filters, tab } = linkProps;
  const { since_year } = filters || {};
  const defaultYear = createDate().subtract(5, 'year').format(DATE_FORMATS.YEAR);
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const activeTab = hasCastingToolAccess ? tab || CASTING_RESULT_TYPES_VALUES.PERFORMED : null;
  const CASTING_TOOL_SUB_TABS = useMemo(
    () => [
      { title: `${TP}.FN_PERFORMED_ROLE`, key: CASTING_RESULT_TYPES_VALUES.PERFORMED },
      { title: sticky ? `${TP}.DESIRED` : `${TP}.DESIRED_ROLE`, key: CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE },
      {
        title: sticky ? `${TP}.FN_ROLE_DEBUT` : `${TP}.ROLE_DEBUT_FINDER`,
        key: CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT,
      },
      {
        title: sticky ? `${TP}.JUMP_IN` : `${TP}.JUMP_IN_REPLACEMENT`,
        key: CASTING_RESULT_TYPES_VALUES.JUMP_IN,
      },
    ],
    [sticky],
  );

  const orgId = useMemo(() => {
    if (salesDemoAccountInfo?.organization?.id) {
      return salesDemoAccountInfo?.organization?.id;
    }

    if (activePageAccessContext?.entityType === ENTITY_TYPES.ORGANIZATION) {
      return activePageAccessContext?.entity?.id;
    }

    return null;
  }, [
    salesDemoAccountInfo?.organization?.id,
    activePageAccessContext?.entityType,
    activePageAccessContext?.entity?.id,
  ]);

  const { data: organization } = useQuery(
    orgQueries.getOrganizations({
      limit: 1,
      filters: {
        id: orgId,
      },
      queryConfig: {
        enabled: !!orgId,
        select: response => response?.data?.[0],
      },
    }),
  );

  const hasAccessToFilters =
    (organization?.stats?.productionCount > 0 &&
      PRODUCTS_WITH_CASTING_TOOL_ACCESS.includes(organization?.subscription?.product?.id)) ||
    !isLoggedIn ||
    isAdmin;

  const onClickHandler = useCallback(
    ({ key: tabKey }) => {
      if (!isLoggedIn) {
        setIsLoginDialog({ isOpen: true });
        return;
      }

      if (!hasCastingToolAccess) {
        showPageAccessPortalForCasting();
        return;
      }

      if (tabKey === CASTING_RESULT_TYPES_VALUES.JUMP_IN) {
        if (!hasAccessToFilters) {
          setIsPermissionDeniedModalOpen(true);
          return;
        }

        setIsJumpInSelectProductionsModalOpen(true);
        return;
      }

      applyFilters({
        baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
        filters: {
          ...filters,
          expression:
            tabKey === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT
              ? convertRoleDebutExpression(filterParams?.[FILTER_TYPES.BOOLEAN_SEARCH])
              : undoRoleDebutExpression(filterParams?.[FILTER_TYPES.BOOLEAN_SEARCH]),
          ...(tabKey === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE
            ? {
                [FILTER_TYPES.SINCE_YEAR]: null,
                [FILTER_TYPES.COUNTRY]: null,
              }
            : {
                [FILTER_TYPES.SINCE_YEAR]: since_year || defaultYear,
              }),
        },
        tab: tabKey,
      });
    },
    [
      isLoggedIn,
      hasCastingToolAccess,
      applyFilters,
      filters,
      filterParams,
      since_year,
      defaultYear,
      setIsLoginDialog,
      hasAccessToFilters,
      setIsJumpInSelectProductionsModalOpen,
      setIsPermissionDeniedModalOpen,
      showPageAccessPortalForCasting,
    ],
  );

  return (
    <TabsNavigation
      tabs={CASTING_TOOL_SUB_TABS}
      handleOnClick={onClickHandler}
      activeTab={activeTab}
      trackingData={trackingData}
      ns="NS_CASTING_TOOL"
      className={classnames(classes.tabs, { [classes.tabs_sticky]: sticky })}
      asDropdown={asDropdown}
      hasAccessToFilters={hasAccessToFilters}
      variant={sticky ? 'orange' : 'dark'}
    />
  );
};

export default CastingResultTypeTabs;
